import React, { useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useStores } from '@/contexts/StoreContext';
import { 
  Copy, 
  Trash2, 
  Unlink,
  ArrowUp,
  ArrowDown,
  ChevronUp,
  ChevronDown,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignVerticalJustifyStart,
  AlignVerticalJustifyCenter,
  AlignVerticalJustifyEnd,
  Rows,
  Columns,
  Group,
  Ungroup,
  AlignHorizontalDistributeStart,
  AlignHorizontalDistributeCenter,
  AlignHorizontalDistributeEnd,
  AlignVerticalDistributeStart,
  AlignVerticalDistributeCenter,
  AlignVerticalDistributeEnd,
  Type,
  Code
} from 'lucide-react';
import styles from './ContextMenu.module.css';

export const ContextMenu: React.FC = observer(() => {
  const { projectStore, uiStore } = useStores();
  const { selectedElementIds, selectedElements } = projectStore;
  const { editingTextId } = uiStore;

  const fontSizeMap = { 'S': 14, 'M': 20, 'L': 28, 'XL': 36 };

  const colors = [
    { color: 'transparent', label: 'Transparent', letter: 'Q' },
    { color: '#f0f0f0', label: 'Light Gray', letter: 'W' },
    { color: '#4ade80', label: 'Green', letter: 'E' },
    { color: '#4ecdc4', label: 'Teal', letter: 'R' }, // Keep R as teal
    { color: '#f97316', label: 'Orange', letter: 'T' }, // T should be more orange
    { color: '#f7ca3e', label: 'Yellow', letter: 'Y' },
    { color: '#8e44ad', label: 'Purple', letter: 'U' },
    { color: '#e74c3c', label: 'Dark Red', letter: 'I' }
  ];

  const backgroundShortcuts = ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K'];

  const ids = Array.from(selectedElementIds.keys());

  const handleStrokeColorChange = useCallback((color: string) => {
    projectStore.updateElementsStyle(ids, { strokeColor: color });
    uiStore.persistentStyle.strokeColor = color;
  }, [projectStore, uiStore, ids]);

  const handleFillColorChange = useCallback((color: string) => {
    projectStore.updateElementsStyle(ids, { fillColor: color });
    uiStore.persistentStyle.fillColor = color;
  }, [projectStore, uiStore, ids]);

  const handleStrokeWidthChange = (width: number) => {
    projectStore.updateElementsStyle(ids, { strokeWidth: width });
    uiStore.persistentStyle.strokeWidth = width;
  };

  const handleStrokeStyleChange = (strokeStyle: 'solid' | 'dashed' | 'dotted') => {
    projectStore.updateElementsStyle(ids, { strokeStyle });
    uiStore.persistentStyle.strokeStyle = strokeStyle;
  };

  const handleFillPatternChange = (fillPattern: 'solid' | 'lines' | 'cross') => {
    projectStore.updateElementsStyle(ids, { fillPattern });
    uiStore.persistentStyle.fillPattern = fillPattern;
  };

  const handleDuplicate = () => projectStore.duplicateSelected();
  const handleDelete = () => projectStore.deleteSelectedElements();

  const handleOpacityChange = (opacity: number) => {
    const newOpacity = opacity / 100;
    projectStore.updateElementsStyle(ids, { opacity: newOpacity });
    uiStore.persistentStyle.opacity = newOpacity;
  };

  const handleFontSizeChange = (size: 'S' | 'M' | 'L' | 'XL') => {
    const fontSize = fontSizeMap[size];
    projectStore.updateElementsStyle(ids, { fontSize });
    uiStore.persistentStyle.fontSize = fontSize;
  };

  const handleBringToFront = () => projectStore.bringToFront(ids);
  const handleSendToBack = () => projectStore.sendToBack(ids);
  const handleBringForward = () => projectStore.bringForward(ids);
  const handleSendBackward = () => projectStore.sendBackward(ids);

  const handleAlign = (type: 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom') => {
    projectStore.alignElements(ids, type);
  };

  const handleDistribute = (type: 'horizontal' | 'vertical') => {
    projectStore.distributeElements({ ids, type });
  };
  
  const handleGroup = () => projectStore.groupElements(ids);
  const handleUngroup = () => {
    const groupElement = selectedElements.find(el => el.type === 'group');
    if (groupElement) {
      projectStore.ungroupElements(groupElement.id);
    }
  };

  const handleTextAlignChange = (textAlign: 'left' | 'center' | 'right') => {
    projectStore.updateElementsStyle(ids, { textAlign });
    uiStore.persistentStyle.textAlign = textAlign;
  };

  const handleTextVerticalAlignChange = (textVerticalAlign: 'top' | 'middle' | 'bottom') => {
    projectStore.updateElementsStyle(ids, { textVerticalAlign });
    uiStore.persistentStyle.textVerticalAlign = textVerticalAlign;
  };

  const handleTextTypeChange = (textType: 'sloppy' | 'pre') => {
    projectStore.updateElementsStyle(ids, { textType });
    uiStore.persistentStyle.textType = textType;
  };

  useEffect(() => {
    if (selectedElementIds.size === 0) return;

    const currentSelectedElements = projectStore.elements.filter(el => selectedElementIds.has(el.id));
    const hasLineArrowTextOrIcon = currentSelectedElements.some(el => el.type === 'line' || el.type === 'arrow' || el.type === 'text' || el.type === 'icon');
    const hasIcons = currentSelectedElements.some(el => el.type === 'icon');

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement || editingTextId !== null) {
        return;
      }
      const key = event.key.toLowerCase();
      const strokeIndex = colors.findIndex(color => color.letter.toLowerCase() === key);
      if (strokeIndex !== -1 && colors[strokeIndex]) {
        event.preventDefault();
        handleStrokeColorChange(colors[strokeIndex].color);
        return;
      }
      const backgroundIndex = backgroundShortcuts.findIndex(bkey => bkey.toLowerCase() === key);
      if (!hasLineArrowTextOrIcon && !hasIcons && backgroundIndex !== -1 && colors[backgroundIndex]) {
        event.preventDefault();
        handleFillColorChange(colors[backgroundIndex].color);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedElementIds, editingTextId, handleStrokeColorChange, handleFillColorChange, projectStore.elements]);

  if (selectedElementIds.size === 0) {
    return null;
  }
  
  const firstElement = selectedElements[0];
  const currentStyle = firstElement?.style;

  const currentOpacity = currentStyle ? Math.round(currentStyle.opacity * 100) : 100;

  const hasGroupableElements = selectedElements.length > 1;
  const hasUngroupableElements = selectedElements.some((el) => el.type === 'group');
  
  const hasTextEditableElements = selectedElements.some(
    (el) => el.type === 'text' || el.type === 'rectangle' || el.type === 'ellipse' || el.type === 'diamond'
  );

  const hasTextElements = selectedElements.some((el) => el.type === 'text');

  const hasVerticalAlignableElements = selectedElements.some(
    (el) => el.type === 'rectangle' || el.type === 'ellipse' || el.type === 'diamond'
  );
  
  const hasConnectedLines = selectedElements.some(el => (el.type === 'line' || el.type === 'arrow') && (el.startConnection || el.endConnection));

  // Icons should not have background color or fill pattern options
  const hasIconElements = selectedElements.some(el => el.type === 'icon');
  const showBackgroundOptions = !hasIconElements;

  const renderContextMenu = () => {
    if (selectedElementIds.size === 0) return null;

    return (
      <div className={styles['contextMenu']}>
        {/* Color Section */}
        <div className={styles['menuSection']}>
          <div className={styles['colorSection']}>
            <div className={styles['colorRow']}>
              <label className={styles['subsectionLabel']}>Stroke</label>
              <div className={styles['colorGrid']}>
                {colors.map(({ color, label, letter }) => (
                  <button
                    key={`stroke-${color}`}
                    className={`${styles['colorButton']} ${currentStyle?.strokeColor === color ? styles['active'] : ''}`}
                    onClick={() => handleStrokeColorChange(color)}
                    title={`Stroke: ${label} (${letter})`}
                  >
                    {color === 'transparent' ? <div className={styles['transparentPattern']} /> : <div style={{ background: color, width: '100%', height: '100%', borderRadius: '6px' }} />}
                    <span className={styles['colorShortcut']}>{letter}</span>
                  </button>
                ))}
              </div>
            </div>
            {showBackgroundOptions && (
              <div className={styles['colorRow']}>
                <label className={styles['subsectionLabel']}>Background</label>
                <div className={styles['colorGrid']}>
                  {colors.map(({ color, label }, index) => (
                    <button
                      key={`fill-${color}`}
                      className={`${styles['colorButton']} ${currentStyle?.fillColor === color ? styles['active'] : ''}`}
                      onClick={() => handleFillColorChange(color)}
                      title={`Fill: ${label} (${backgroundShortcuts[index]})`}
                    >
                      {color === 'transparent' ? <div className={styles['transparentPattern']} /> : <div style={{ background: color, width: '100%', height: '100%', borderRadius: '6px' }} />}
                      <span className={styles['colorShortcut']}>{backgroundShortcuts[index]}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Details Section */}
        <div className={styles['menuSection']}>
          <label className={styles['sectionLabel']}>Details</label>
          <div className={styles['subsection']}>
            <label className={styles['subsectionLabel']}>Stroke width</label>
            <div className={styles['buttonGrid']}>
              {[2, 4, 8].map(w => (
                <button key={w} className={`${styles['optionButton']} ${currentStyle?.strokeWidth === w ? styles['active'] : ''}`} onClick={() => handleStrokeWidthChange(w)} title={`${w}px width`}>
                  <div className={styles['strokePreview']} style={{ height: `${w}px` }} />
                </button>
              ))}
            </div>
          </div>
          <div className={styles['subsection']}>
            <label className={styles['subsectionLabel']}>Stroke style</label>
            <div className={styles['buttonGrid']}>
              {['solid', 'dashed', 'dotted'].map(s => (
                <button key={s} className={`${styles['optionButton']} ${currentStyle?.strokeStyle === s ? styles['active'] : ''}`} onClick={() => handleStrokeStyleChange(s as any)} title={s}>
                  <div className={styles['strokeStylePreview']} data-style={s} />
                </button>
              ))}
            </div>
          </div>
          {showBackgroundOptions && (
            <div className={styles['subsection']}>
              <label className={styles['subsectionLabel']}>Fill style</label>
              <div className={styles['buttonGrid']}>
                {['solid', 'lines', 'cross'].map(p => (
                  <button key={p} className={`${styles['optionButton']} ${currentStyle?.fillPattern === p ? styles['active'] : ''}`} onClick={() => handleFillPatternChange(p as any)} title={p}>
                    <div className={styles['fillPatternPreview']} data-pattern={p} />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Opacity Section */}
        <div className={styles['menuSection']}>
           <div className={styles['sliderContainer']}>
             <input
                type="range"
                min="0"
                max="100"
                value={currentOpacity}
                className={styles['opacitySlider']}
                onChange={(e) => handleOpacityChange(parseInt(e.target.value, 10))}
              />
           </div>
        </div>

        {/* Arrange Section */}
        <div className={styles['menuSection']}>
          <label className={styles['sectionLabel']}>Arrange</label>
          {hasGroupableElements && (
            <div className={styles['buttonGrid']}>
              <button className={styles['optionButton']} onClick={() => handleAlign('left')} title="Align left"><AlignHorizontalDistributeStart size={20} /></button>
              <button className={styles['optionButton']} onClick={() => handleAlign('center')} title="Align center"><AlignHorizontalDistributeCenter size={20} /></button>
              <button className={styles['optionButton']} onClick={() => handleAlign('right')} title="Align right"><AlignHorizontalDistributeEnd size={20} /></button>
              <button className={styles['optionButton']} onClick={() => handleDistribute('horizontal')} title="Distribute horizontal"><Rows size={20} /></button>
              
              <button className={styles['optionButton']} onClick={() => handleAlign('top')} title="Align top"><AlignVerticalDistributeStart size={20} /></button>
              <button className={styles['optionButton']} onClick={() => handleAlign('middle')} title="Align vertical center"><AlignVerticalDistributeCenter size={20} /></button>
              <button className={styles['optionButton']} onClick={() => handleAlign('bottom')} title="Align bottom"><AlignVerticalDistributeEnd size={20} /></button>
              <button className={styles['optionButton']} onClick={() => handleDistribute('vertical')} title="Distribute vertical"><Columns size={20} /></button>
            </div>
          )}
        </div>

        {/* Layers & Actions */}
        <div className={styles['menuSection']}>
          <div className={styles['subsection']}>
            <label className={styles['sectionLabel']}>Layer</label>
            <div className={styles['buttonGrid']}>
              <button className={styles['optionButton']} onClick={handleBringToFront} title='Bring to front'><ArrowUp /></button>
              <button className={styles['optionButton']} onClick={handleSendToBack} title='Send to back'><ArrowDown /></button>
              <button className={styles['optionButton']} onClick={handleBringForward} title='Bring forward'><ChevronUp /></button>
              <button className={styles['optionButton']} onClick={handleSendBackward} title='Send backward'><ChevronDown /></button>
            </div>
          </div>
          <div className={styles['subsection']}>
            <label className={styles['sectionLabel']}>Actions</label>
            <div className={styles['buttonGrid']}>
              <button className={styles['optionButton']} onClick={handleDuplicate} title='Duplicate'><Copy /></button>
              <button className={styles['optionButton']} onClick={handleDelete} title='Delete'><Trash2 /></button>
              {hasGroupableElements && <button className={styles['optionButton']} onClick={handleGroup} title='Group'><Group /></button>}
              {hasUngroupableElements && <button className={styles['optionButton']} onClick={handleUngroup} title='Ungroup'><Ungroup /></button>}
              {hasConnectedLines && <button className={styles['optionButton']} title='Disconnect'><Unlink /></button>}
            </div>
          </div>
        </div>

        {/* Text options */}
        {hasTextEditableElements && (
          <div className={styles['menuSection']}>
            <label className={styles['sectionLabel']}>Text</label>
            <div className={styles['subsection']}>
              <label className={styles['subsectionLabel']}>Font size</label>
              <div className={styles['buttonGrid']}>
                {['S', 'M', 'L', 'XL'].map(size => (
                  <button key={size} className={`${styles['optionButton']} ${styles['fontSizeButton']} ${
                    currentStyle?.fontSize === (fontSizeMap[size as keyof typeof fontSizeMap]) ? styles['active'] : ''
                  }`} onClick={() => handleFontSizeChange(size as any)} title={`Font size ${size}`}>
                    {size}
                  </button>
                ))}
              </div>
            </div>
            {hasTextElements && (
              <div className={styles['subsection']}>
                <label className={styles['subsectionLabel']}>Text type</label>
                <div className={styles['buttonGrid']}>
                  <button 
                    className={`${styles['optionButton']} ${currentStyle?.textType === 'sloppy' ? styles['active'] : ''}`} 
                    onClick={() => handleTextTypeChange('sloppy')} 
                    title='Sloppy text (handwritten style)'
                  >
                    <Type />
                  </button>
                  <button 
                    className={`${styles['optionButton']} ${currentStyle?.textType === 'pre' ? styles['active'] : ''}`} 
                    onClick={() => handleTextTypeChange('pre')} 
                    title='Pre text (monospaced, for code)'
                  >
                    <Code />
                  </button>
                </div>
              </div>
            )}
            <div className={styles['subsection']}>
              <label className={styles['subsectionLabel']}>Alignment</label>
              <div className={styles['buttonGrid']}>
                <button className={`${styles['optionButton']} ${currentStyle?.textAlign === 'left' ? styles['active'] : ''}`} onClick={() => handleTextAlignChange('left')} title='Align text left'><AlignLeft /></button>
                <button className={`${styles['optionButton']} ${currentStyle?.textAlign === 'center' ? styles['active'] : ''}`} onClick={() => handleTextAlignChange('center')} title='Align text center'><AlignCenter /></button>
                <button className={`${styles['optionButton']} ${currentStyle?.textAlign === 'right' ? styles['active'] : ''}`} onClick={() => handleTextAlignChange('right')} title='Align text right'><AlignRight /></button>
                {hasVerticalAlignableElements && (
                  <>
                    <button className={`${styles['optionButton']} ${currentStyle?.textVerticalAlign === 'top' ? styles['active'] : ''}`} onClick={() => handleTextVerticalAlignChange('top')} title='Align text top'><AlignVerticalJustifyStart /></button>
                    <button className={`${styles['optionButton']} ${currentStyle?.textVerticalAlign === 'middle' ? styles['active'] : ''}`} onClick={() => handleTextVerticalAlignChange('middle')} title='Align text middle'><AlignVerticalJustifyCenter /></button>
                    <button className={`${styles['optionButton']} ${currentStyle?.textVerticalAlign === 'bottom' ? styles['active'] : ''}`} onClick={() => handleTextVerticalAlignChange('bottom')} title='Align text bottom'><AlignVerticalJustifyEnd /></button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return renderContextMenu();
}); 